/**
 * Public demo. No login, no database, no contact with real data.
 *
 * This is the sales surface: a plausible live tournament with movement on every
 * refresh, and every ad zone populated with sample creative so a prospective
 * sponsor sees exactly the inventory they would be buying.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { LeaderboardPayload, ScorecardPayload } from '@fairwaylive/shared';
import { AdProvider } from '../lib/adClient.tsx';
import { AdZone, InterstitialAd } from '../components/AdZone.tsx';
import { Leaderboard } from '../components/Leaderboard.tsx';
import { DemoBadge, ErrorNote, HeroCarousel, LiveDot, Spinner } from '../components/Shell.tsx';
import { api, post } from '../lib/api.ts';
import { useFetch, useLive } from '../lib/useLive.ts';

interface DemoSummary {
  tournamentName: string;
  subtitle: string;
  clubName: string;
  presentedBy: string;
  heroSceneKey: string;
  roundNumber: number;
  roundCount: number;
  coursePar: number;
  playerCount: number;
  boards: Array<{ slug: string; name: string; scope: string; formatKey: string }>;
  tickSeconds: number;
  notice: string;
}

export function DemoPage() {
  const { data: summary, loading, error } = useFetch<DemoSummary>('/api/demo');
  const [board, setBoard] = useState('gross');

  if (loading) return <Spinner label="Building the demo tournament" />;
  if (error || !summary) {
    return (
      <div className="mx-auto max-w-[900px] px-4 py-16">
        <ErrorNote message={error ?? 'Demo unavailable'} hint="Check that the API is running on port 4000." />
      </div>
    );
  }

  return (
    <AdProvider surface="leaderboard" tournamentId="demo-tournament" isDemo>
      <DemoBody summary={summary} board={board} setBoard={setBoard} />
      <InterstitialAd />
    </AdProvider>
  );
}

function DemoBody({
  summary,
  board,
  setBoard,
}: {
  summary: DemoSummary;
  board: string;
  setBoard: (slug: string) => void;
}) {
  const live = useLive<LeaderboardPayload>({
    fetchPath: `/api/demo/leaderboard/${board}`,
    streamPath: `/api/demo/stream/${board}`,
    pollSeconds: summary.tickSeconds,
  });
  const [selected, setSelected] = useState<string | null>(null);

  return (
    <div className="pb-24">
      {/* Persistent demo label — required so nothing here is mistaken for real. */}
      <div className="sticky top-[57px] z-20 flex flex-wrap items-center justify-between gap-2 border-b border-[color-mix(in_oklab,var(--color-gold-500)_35%,transparent)] bg-[color-mix(in_oklab,var(--color-gold-500)_10%,var(--color-fairway-900))] px-4 py-2">
        <DemoBadge />
        <p className="text-[11px] text-[var(--color-muted)]">
          Fictional players, clubs and sponsors · isolated from live data and analytics
        </p>
      </div>

      <HeroCarousel sceneKeys={[summary.heroSceneKey, 'bushveld_noon', 'coastal_wind']} height="h-[300px] sm:h-[380px]">
        <div className="mx-auto w-full max-w-[1240px] px-4 pb-8">
          <p className="eyebrow text-[var(--color-sand)]">
            Presented by {summary.presentedBy} · {summary.clubName}
          </p>
          <h1 className="board-title mt-1 text-[34px] font-bold leading-none sm:text-[52px]">{summary.tournamentName}</h1>
          <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-[var(--color-sand)]">
            <span>
              Round {summary.roundNumber} of {summary.roundCount}
            </span>
            <span>Par {summary.coursePar}</span>
            <span>{summary.playerCount} in the field</span>
            <LiveDot transport={live.transport} />
          </div>
        </div>
      </HeroCarousel>

      <div className="mx-auto max-w-[1240px] px-4">
        <div className="pt-4">
          <AdZone zoneKey="top_banner" hideWhenEmpty={false} />
        </div>

        <div className="mt-5 grid gap-6 lg:grid-cols-[minmax(0,1fr)_300px]">
          <div>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap gap-2" role="tablist" aria-label="Leaderboards">
                {summary.boards.map((b) => (
                  <button
                    key={b.slug}
                    type="button"
                    role="tab"
                    aria-selected={board === b.slug}
                    onClick={() => setBoard(b.slug)}
                    className={`btn !py-1.5 !text-[13px] ${board === b.slug ? 'btn-primary' : 'btn-ghost'}`}
                  >
                    {b.name}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <Link to="/demo/bigscreen" className="btn btn-ghost !py-1.5 !text-[13px]">
                  ⛶ Big screen
                </Link>
                <button
                  type="button"
                  onClick={() => void post('/api/demo/advance', {}).then(() => live.refresh())}
                  className="btn btn-gold !py-1.5 !text-[13px]"
                  title="Play the next hole for the field"
                >
                  Advance play
                </button>
              </div>
            </div>

            {live.data ? (
              <>
                <div className="mb-2 flex items-center justify-between text-[11px] text-[var(--color-muted-dim)]">
                  <span>
                    {live.data.formatLabel} · {live.data.courseName}
                  </span>
                  <span>
                    updated {live.updatedAt ? new Date(live.updatedAt).toLocaleTimeString('en-ZA') : '—'} · auto every{' '}
                    {summary.tickSeconds}s
                  </span>
                </div>
                <Leaderboard
                  payload={live.data}
                  adEveryNRows={8}
                  onSelectRow={(row) => setSelected(row.entryId === selected ? null : row.entryId)}
                />
              </>
            ) : (
              <Spinner label="Loading board" />
            )}

            {selected ? <DemoScorecard entryId={selected} onClose={() => setSelected(null)} /> : null}

            <div className="mt-6">
              <AdZone zoneKey="affiliate_slot" hideWhenEmpty={false} className="max-w-[360px]" />
            </div>
          </div>

          <aside className="space-y-4">
            <AdZone zoneKey="sidebar_primary" hideWhenEmpty={false} />
            <AdZone zoneKey="sidebar_secondary" hideWhenEmpty={false} />
            <div className="card p-4">
              <h3 className="eyebrow mb-2">What a sponsor gets</h3>
              <ul className="space-y-2 text-[13px] text-[var(--color-muted)]">
                <li>· Banner, sidebar, in-feed and footer inventory</li>
                <li>· Interstitial takeover every 2 minutes with a skip timer</li>
                <li>· Full-bleed clubhouse big-screen rotation</li>
                <li>· Impression, click and dwell reporting per placement</li>
              </ul>
              <Link to="/rate-card" className="btn btn-primary mt-4 w-full">
                See the rate card
              </Link>
            </div>
            <div className="card p-4">
              <h3 className="eyebrow mb-2">Running an event?</h3>
              <p className="text-[13px] text-[var(--color-muted)]">
                One event, 40 players and the full board set is free. Ads stay on — that is how it pays for itself.
              </p>
              <Link to="/pricing" className="btn btn-ghost mt-3 w-full">
                Compare plans
              </Link>
            </div>
          </aside>
        </div>
      </div>

      {/* Sticky footer unit travels with the viewer, as sold. */}
      <div className="fixed bottom-0 left-0 right-0 z-20 border-t border-[color-mix(in_oklab,var(--color-fairway-400)_22%,transparent)] bg-[color-mix(in_oklab,var(--color-fairway-900)_94%,transparent)] px-3 py-2 backdrop-blur">
        <div className="mx-auto max-w-[760px]">
          <AdZone zoneKey="footer_banner" />
        </div>
      </div>
    </div>
  );
}

/** Read-only card view, showing what the mobile scoring flow captures. */
function DemoScorecard({ entryId, onClose }: { entryId: string; onClose: () => void }) {
  const [card, setCard] = useState<ScorecardPayload | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api<ScorecardPayload>(`/api/demo/scorecard/${entryId}`)
      .then((payload) => {
        if (!cancelled) setCard(payload);
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [entryId]);

  if (error) return <ErrorNote message={error} />;
  if (!card) return <Spinner label="Loading card" />;

  const out = card.holes.filter((h) => h.holeNumber <= 9);
  const inn = card.holes.filter((h) => h.holeNumber > 9);

  return (
    <div className="card mt-4 p-4">
      <div className="mb-3 flex items-start justify-between">
        <div>
          <h3 className="board-title text-lg font-bold">{card.playerName}</h3>
          <p className="text-xs text-[var(--color-muted)]">
            {card.formatLabel} · {card.teeName} tees · playing off {card.handicapAllowance} · thru {card.thru}
          </p>
        </div>
        <button type="button" onClick={onClose} className="btn btn-ghost !px-3 !py-1 !text-xs">
          Close ✕
        </button>
      </div>
      <div className="space-y-3 overflow-x-auto">
        <CardNine holes={out} label="Out" total={card.outStrokes} />
        <CardNine holes={inn} label="In" total={card.inStrokes} />
      </div>
      <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm">
        <span className="text-[var(--color-muted)]">
          Gross <strong className="tabular text-[var(--color-chalk)]">{card.totalStrokes ?? '—'}</strong>
        </span>
        <span className="text-[var(--color-muted)]">
          {card.formatLabel} <strong className="tabular text-[var(--color-fairway-300)]">{card.formatTotalLabel}</strong>
        </span>
      </div>
    </div>
  );
}

function CardNine({
  holes,
  label,
  total,
}: {
  holes: ScorecardPayload['holes'];
  label: string;
  total: number | null;
}) {
  if (!holes.length) return null;
  return (
    <table className="w-full min-w-[520px] border-collapse text-center">
      <thead>
        <tr className="text-[10px] uppercase tracking-wider text-[var(--color-muted-dim)]">
          <th className="py-1 text-left">Hole</th>
          {holes.map((h) => (
            <th key={h.holeNumber} className="py-1">
              {h.holeNumber}
            </th>
          ))}
          <th className="py-1">{label}</th>
        </tr>
      </thead>
      <tbody>
        <tr className="text-[11px] text-[var(--color-muted)]">
          <td className="text-left">Par / SI</td>
          {holes.map((h) => (
            <td key={h.holeNumber} className="tabular py-0.5">
              {h.par}
              <span className="text-[var(--color-muted-dim)]">/{h.strokeIndex}</span>
            </td>
          ))}
          <td className="tabular">{holes.reduce((s, h) => s + h.par, 0)}</td>
        </tr>
        <tr>
          <td className="text-left text-[11px] text-[var(--color-muted)]">Score</td>
          {holes.map((h) => {
            const delta = h.strokes === null ? null : h.strokes - h.par;
            const tone =
              delta === null
                ? 'text-[var(--color-fairway-600)]'
                : delta <= -2
                  ? 'bg-[var(--color-gold-500)] text-[#1a1204]'
                  : delta === -1
                    ? 'bg-[var(--color-fairway-300)] text-[var(--color-fairway-950)]'
                    : delta === 0
                      ? 'text-[var(--color-chalk)]'
                      : delta === 1
                        ? 'text-[var(--color-sky-300)]'
                        : 'text-[var(--color-rose-sun)]';
            return (
              <td key={h.holeNumber} className="py-1">
                <span className={`tabular inline-grid h-7 w-7 place-items-center rounded font-bold ${tone}`}>
                  {h.strokes ?? '·'}
                </span>
                {h.shotsReceived > 0 ? (
                  <span className="block text-[8px] leading-none text-[var(--color-gold-500)]">
                    {'•'.repeat(Math.min(3, h.shotsReceived))}
                  </span>
                ) : null}
              </td>
            );
          })}
          <td className="tabular font-bold">{total ?? '—'}</td>
        </tr>
        <tr className="text-[10px] text-[var(--color-muted-dim)]">
          <td className="text-left">Pts</td>
          {holes.map((h) => (
            <td key={h.holeNumber} className="tabular">
              {h.formatLabel ?? '·'}
            </td>
          ))}
          <td />
        </tr>
      </tbody>
    </table>
  );
}
